import type { ActiveEncodeMode, CursorOverlayMessage, MoveRect, TileImageCodec } from "@srd/shared";
import { decodeTileFrameBinary } from "./tileFrameCodec";

export interface CompositorStats {
  bitrateKbps: number | null;
  encodeMs: number | null;
  fps: number | null;
  mode: ActiveEncodeMode | null;
  lastFrameId: number | null;
  tilesApplied: number;
}

export interface CursorState {
  x: number;
  y: number;
  visible: boolean;
  hotspotX: number;
  hotspotY: number;
}

export class TileCompositor {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private scratch: HTMLCanvasElement;
  private scratchCtx: CanvasRenderingContext2D;
  private width = 0;
  private height = 0;
  private cursor: CursorState = {
    x: 0,
    y: 0,
    visible: false,
    hotspotX: 0,
    hotspotY: 0,
  };
  private stats: CompositorStats = {
    bitrateKbps: null,
    encodeMs: null,
    fps: null,
    mode: null,
    lastFrameId: null,
    tilesApplied: 0,
  };
  private decodeQueue: Promise<void> = Promise.resolve();

  constructor(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("2d context unavailable");
    this.canvas = canvas;
    this.ctx = ctx;
    this.scratch = document.createElement("canvas");
    const sctx = this.scratch.getContext("2d", { alpha: false });
    if (!sctx) throw new Error("scratch 2d context unavailable");
    this.scratchCtx = sctx;
  }

  getCursor(): CursorState {
    return { ...this.cursor };
  }

  getStats(): CompositorStats {
    return { ...this.stats };
  }

  getFramebufferSize(): { width: number; height: number } {
    return { width: this.width, height: this.height };
  }

  setCursorFromControl(msg: CursorOverlayMessage): void {
    this.cursor = {
      x: msg.x,
      y: msg.y,
      visible: msg.visible,
      hotspotX: msg.hotX ?? 0,
      hotspotY: msg.hotY ?? 0,
    };
  }

  setStatsFromControl(partial: {
    fps?: number;
    encodeMode?: ActiveEncodeMode;
    throughputKbps?: number;
  }): void {
    if (partial.fps != null) this.stats.fps = partial.fps;
    if (partial.encodeMode) this.stats.mode = partial.encodeMode;
    if (partial.throughputKbps != null) this.stats.bitrateKbps = partial.throughputKbps;
  }

  handleBinary(buffer: ArrayBuffer): void {
    this.decodeQueue = this.decodeQueue
      .then(() => this.applyBinary(buffer))
      .catch(() => undefined);
  }

  private async applyBinary(buffer: ArrayBuffer): Promise<void> {
    const decoded = decodeTileFrameBinary(buffer);
    if (!decoded) return;
    const { meta, tileBytes } = decoded;
    this.ensureSize(meta.screenW, meta.screenH);
    this.stats.lastFrameId = meta.frameId;

    if (meta.moves?.length) {
      for (const move of meta.moves) {
        this.copyRect(move);
      }
    }

    for (let i = 0; i < meta.tiles.length; i++) {
      const rect = meta.tiles[i];
      const bytes = tileBytes[i];
      if (!rect || !bytes) continue;
      await this.drawTile(rect.x, rect.y, rect.w, rect.h, meta.codec, bytes);
      this.stats.tilesApplied += 1;
    }
  }

  private ensureSize(width: number, height: number): void {
    if (width === this.width && height === this.height) return;
    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;
    this.scratch.width = width;
    this.scratch.height = height;
    this.ctx.fillStyle = "#111";
    this.ctx.fillRect(0, 0, width, height);
  }

  private copyRect(r: MoveRect): void {
    if (!this.width || !this.height) return;
    this.scratchCtx.drawImage(this.canvas, 0, 0);
    this.ctx.drawImage(
      this.scratch,
      r.srcX,
      r.srcY,
      r.w,
      r.h,
      r.dstX,
      r.dstY,
      r.w,
      r.h,
    );
  }

  private async drawTile(
    x: number,
    y: number,
    w: number,
    h: number,
    codec: TileImageCodec,
    data: Uint8Array,
  ): Promise<void> {
    const mime =
      codec === "webp" ? "image/webp" : codec === "png" ? "image/png" : "image/jpeg";
    const blob = new Blob([data.slice()], { type: mime });
    const bitmap = await createImageBitmap(blob);
    try {
      this.ctx.drawImage(bitmap, x, y, w, h);
    } finally {
      bitmap.close();
    }
  }
}
